/**
 * Ownership search criteria: the vocabulary and the wording shared by the search
 * forms, the shareholder-structure builder, the read-only criteria panels, the results column
 * and the company page.
 *
 * The keys are the backend's (`company_ownership_summary.structures` and the request
 * validation), so they must not be renamed here alone. The labels and rules are the mockup's
 * (canvas page C). Kept free of `fields.js` / `uiOptions.js` imports: both import from here.
 */
import { formatOwnershipShare } from './ownership';
import { isProfilerAdvancedCriteriaOwnershipDisabled } from './constants/variables';

export const OWNERSHIP_BASIS = {
  DIRECT: 'direct',
  ULTIMATE: 'ultimate',
  INDIVIDUALS: 'individuals'
};

export const DEFAULT_OWNERSHIP_BASIS = OWNERSHIP_BASIS.DIRECT;

export const OWNERSHIP_BASES = [
  {
    value: OWNERSHIP_BASIS.DIRECT,
    name: 'Direct shareholders',
    short: 'Direct',
    noun: 'direct shareholder',
    explanation: 'the registered shareholders of the company itself'
  },
  {
    value: OWNERSHIP_BASIS.ULTIMATE,
    name: 'Ultimate shareholders',
    short: 'Ultimate',
    noun: 'ultimate shareholder',
    explanation: 'the owners at the top of the chain, people and companies'
  },
  {
    value: OWNERSHIP_BASIS.INDIVIDUALS,
    name: 'Individuals only',
    short: 'Individuals',
    noun: 'individual',
    explanation: 'the people at the top, with their economic share'
  }
];

// Canonical order: the builder lists them in it, and `sortStructures` keeps the stored value in
// it so `compareFields` (a JSON comparison) does not see a reordered pick as a change.
export const OWNERSHIP_STRUCTURES = [
  {
    value: 'highly_fragmented',
    name: 'Highly fragmented',
    rule: 'no shareholder holds 10% or more',
    needsFullDisclosure: true
  },
  {
    value: 'fragmented',
    name: 'Fragmented',
    rule: 'no shareholder holds 25% or more',
    needsFullDisclosure: true
  },
  {
    value: 'significant_minority',
    name: 'Significant minority',
    rule: 'a shareholder holds 25% to under 50%',
    needsFullDisclosure: false
  },
  {
    // No full-disclosure requirement: a third holder of 45% or more would push the total past 100%.
    value: 'joint_venture',
    name: 'Joint venture',
    rule: 'two shareholders hold 45% to 55% each',
    needsFullDisclosure: false
  },
  {
    value: 'majority_owned',
    name: 'Majority owned',
    rule: 'one shareholder holds more than 50% and less than 95%',
    needsFullDisclosure: false
  },
  {
    value: 'wholly_owned',
    name: 'Wholly owned',
    rule: 'one shareholder holds 95% or more',
    needsFullDisclosure: false
  }
];

export const LISTING_STATUSES = [
  { value: 'listed', name: 'Publicly listed' },
  { value: 'not_listed', name: 'Not publicly listed' }
];

const STRUCTURE_ORDER = OWNERSHIP_STRUCTURES.map((s) => s.value);

const findStructure = (key) => OWNERSHIP_STRUCTURES.find((s) => s.value === key);

export const findOwnershipBasis = (basis) =>
  OWNERSHIP_BASES.find((b) => b.value === basis) ||
  OWNERSHIP_BASES.find((b) => b.value === DEFAULT_OWNERSHIP_BASIS);

/** The basis stored in a radio field value (`['ultimate']`), or a plain string, or the default. */
export const resolveOwnershipBasis = (basisValue) => {
  const raw = Array.isArray(basisValue) ? basisValue[0] : basisValue;
  return OWNERSHIP_BASES.some((b) => b.value === raw) ? raw : DEFAULT_OWNERSHIP_BASIS;
};

/** Known keys only, de-duplicated, in canonical order. */
export const sortStructures = (keys = []) =>
  STRUCTURE_ORDER.filter((key) => Array.isArray(keys) && keys.includes(key));

export const getStructureName = (key) => findStructure(key)?.name || key;

/** "a", "a or b", "a, b or c". */
export const joinAlternatives = (items = []) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`;

export const getListingStatusName = (value) =>
  LISTING_STATUSES.find((s) => s.value === value)?.name || null;

/**
 * Like CAGR period: clearing the last structure clears the chain position, which is only
 * meaningful with a structure. Mutates the form fields. (Field names as in fields.js.)
 */
export const clearOwnershipBasisWithoutStructures = (fields) => {
  if (!fields?.ownershipStructures?.length && fields?.ownershipBasis?.length) {
    fields.ownershipBasis = [];
  }
};

/** A structure is chosen but no chain position yet: like an empty CAGR period, Search waits. */
export const isOwnershipBasisMissing = (fields) =>
  !!fields?.ownershipStructures?.length && !fields?.ownershipBasis?.length;

/**
 * Brings a search request body in line with the ownership contract:
 * - the basis is only sent alongside a structure, so a basis on its own never changes the
 *   results or the backend's cache key;
 * - a structure without a basis counts direct shareholders;
 * - while the criteria are switched off, the keys are not sent at all.
 * Mutates and returns `body`.
 */
export const normalizeOwnershipRequest = (body) => {
  if (!body) return body;
  if (isProfilerAdvancedCriteriaOwnershipDisabled) {
    delete body.listingStatus;
    delete body.ownershipStructures;
    delete body.ownershipBasis;
    return body;
  }
  const structures = sortStructures(body.ownershipStructures);
  if ('ownershipStructures' in body) {
    body.ownershipStructures = structures;
  }
  if ('ownershipStructures' in body || 'ownershipBasis' in body) {
    body.ownershipBasis = structures.length ? resolveOwnershipBasis(body.ownershipBasis) : null;
  }
  return body;
};

/** The searched presets that a company carries on the given basis. */
export const getMatchedStructures = (summary, basis, searchedKeys = []) => {
  const structures = summary?.[basis]?.structures;
  if (!Array.isArray(structures) || !Array.isArray(searchedKeys)) return [];
  return sortStructures(structures.filter((key) => searchedKeys.includes(key)));
};

/**
 * A stake from the ownership summary, as stored (two decimals, never rounded): rounding would
 * print 94.96 as "95.0%" next to the label Majority owned. Values outside 0–100 come from share
 * amounts larger than the registered capital and are not shown.
 */
export const formatOwnershipPct = (pct) => {
  if (pct === null || pct === undefined || pct === '') return null;
  const n = Number(pct);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? formatOwnershipShare(n) : null;
};
